import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, Trophy, Users, Star, ArrowLeft, ChevronRight,
  Loader, TrendingUp, Medal, Award
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollegeRankingsPage = () => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchRankings();
  }, [page]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/codearena/college-rankings`, {
        params: { page, limit: 20 }
      });
      setRankings(res.data.rankings || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      console.error('Failed to fetch rankings');
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return (
      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
        <Trophy className="text-white" size={24} />
      </div>
    );
    if (rank === 2) return (
      <div className="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center shadow">
        <Medal className="text-white" size={24} />
      </div>
    );
    if (rank === 3) return (
      <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center shadow">
        <Award className="text-white" size={24} />
      </div>
    );
    return (
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-bold text-lg">
        {rank}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <Link 
            to="/codearena" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Back to EvalentX Arena
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <GraduationCap className="text-white" size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">College Rankings</h1>
              <p className="text-gray-400 mt-1">Top colleges based on student performance</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rankings */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-purple-400" size={32} />
          </div>
        ) : rankings.length > 0 ? (
          <div className="space-y-4">
            {/* Top 3 Highlight */}
            {page === 1 && (
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                {rankings.slice(0, 3).map((college, idx) => (
                  <div 
                    key={idx}
                    className={`bg-gradient-to-br ${
                      idx === 0 ? 'from-yellow-500/20 to-amber-600/20 border-yellow-500/30' :
                      idx === 1 ? 'from-gray-400/20 to-gray-500/20 border-gray-400/30' :
                      'from-amber-600/20 to-orange-700/20 border-amber-600/30'
                    } border rounded-2xl p-6 text-center`}
                  >
                    <div className="flex justify-center mb-4">
                      {getRankBadge(college.rank)}
                    </div>
                    <h3 className="font-bold text-lg text-white mb-2">{college.college_name}</h3>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <p className="text-2xl font-bold text-purple-400">{college.avg_score}</p>
                        <p className="text-xs text-gray-500">Avg Score</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-cyan-400">{college.total_students}</p>
                        <p className="text-xs text-gray-500">Students</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Full Rankings List */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/10">
                <h2 className="font-bold text-white flex items-center gap-2">
                  <TrendingUp className="text-purple-400" size={20} />
                  All Rankings
                </h2>
              </div>
              <div className="divide-y divide-white/5">
                {rankings.map((college) => (
                  <div 
                    key={college.rank}
                    className="px-6 py-4 flex items-center gap-4 hover:bg-white/5 transition-colors"
                    data-testid={`college-rank-${college.rank}`}
                  >
                    <div className="w-10 h-10 flex items-center justify-center">
                      {college.rank <= 3 ? (
                        getRankBadge(college.rank)
                      ) : (
                        <span className="text-lg font-bold text-gray-500">#{college.rank}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{college.college_name}</h3>
                    </div>
                    <div className="flex items-center gap-8 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-purple-400">{college.avg_score}</p>
                        <p className="text-xs text-gray-500">Avg Score</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-cyan-400">{college.total_students}</p>
                        <p className="text-xs text-gray-500">Students</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-green-400">{college.max_score}</p>
                        <p className="text-xs text-gray-500">Top Score</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white/5 text-gray-400 rounded-lg hover:bg-white/10 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                  className="px-4 py-2 bg-white/5 text-gray-400 rounded-lg hover:bg-white/10 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-12 text-center">
            <GraduationCap className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-white mb-2">No Rankings Yet</h3>
            <p className="text-gray-500">
              Colleges need at least 3 registered students to appear in rankings
            </p>
          </div>
        )}

        {/* Info Card */}
        <div className="mt-8 bg-purple-500/10 border border-purple-500/20 rounded-2xl p-6">
          <h3 className="font-bold text-purple-400 mb-2">How are colleges ranked?</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• Rankings are based on the average EvalentX Arena score of all registered students</li>
            <li>• Colleges must have at least 3 students to appear in rankings</li>
            <li>• Scores are updated in real-time as students solve problems and participate in contests</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CollegeRankingsPage;
